import { useNavigate } from 'react-router-dom'
import NaturalBackground from '../components/NaturalBackground'
import ThemeToggle from '../components/ThemeToggle'
import { useTheme } from '../components/ThemeProvider'
import MindPolishLogo from '../components/MindPolishLogo'
import DopaLogo from '../components/DopaLogo'

const Landing = () => {
  const navigate = useNavigate()
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  // Function to navigate and ensure scroll to top
  const navigateToHome = () => {
    navigate('/home');
    // Ensure scroll to top
    window.scrollTo(0, 0);
  }

  return (
    <NaturalBackground variant="forest">
      <div className="min-h-screen flex flex-col">
        {/* Navigation */}
        <header className="py-4 sm:py-6 px-4 sm:px-8 flex items-center justify-between bg-white/40 dark:bg-sage-800/40 backdrop-blur-sm border-b border-sage-100/30 dark:border-sage-700/30">
          <MindPolishLogo size="sm" />
          <div className="flex items-center space-x-3">
            <ThemeToggle size="sm" />
            <button 
              onClick={navigateToHome}
              className="px-4 py-2 bg-mint-100/60 hover:bg-mint-200/50 
                         dark:bg-mint-900/30 dark:hover:bg-mint-800/40
                         text-mint-600 dark:text-mint-300 rounded-xl text-sm font-light 
                         border border-mint-100/40 dark:border-mint-700/40 
                         hover:border-mint-200/50 dark:hover:border-mint-600/50
                         transition-colors duration-300 focus:outline-none focus:ring-2 
                         focus:ring-mint-100/50 dark:focus:ring-mint-700/30"
            >
              Enter App
            </button>
            {/* White Circle Logo - Beside Enter App button */}
            <a 
              href="https://bolt.new" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block hover:opacity-80 transition-opacity duration-300"
            >
              <img 
                src="/white_circle_360x360.png" 
                alt="Powered by Bolt" 
                className="w-8 h-8"
              />
            </a>
          </div>
        </header>

        {/* Hero Section */}
        <section className="flex-1 flex flex-col items-center justify-center px-4 sm:px-8 py-12 sm:py-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mx-auto mb-6">
              <MindPolishLogo size="xl" className="justify-center" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-light text-sage-600 dark:text-sage-200 mb-6">
              Mind Polish: A Gentle Space for Mental Wellbeing
            </h1>
            <p className="text-lg sm:text-xl font-light text-sage-500 dark:text-sage-300 mb-8 leading-relaxed max-w-2xl mx-auto">
              A calming sanctuary designed for neurodivergent minds and anyone seeking a peaceful approach to mental wellness.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-12">
              <button 
                onClick={navigateToHome}
                className="w-full sm:w-auto px-6 py-4 bg-sage-100/80 hover:bg-sage-200/60 
                           dark:bg-sage-700/40 dark:hover:bg-sage-600/50
                           text-sage-600 dark:text-sage-200 rounded-2xl text-base font-light 
                           border border-sage-200/50 dark:border-sage-600/40 
                           hover:border-sage-300/60 dark:hover:border-sage-500/50
                           transition-colors duration-300 focus:outline-none focus:ring-4 
                           focus:ring-sage-100 dark:focus:ring-sage-600/30
                           shadow-sm hover:shadow-md"
              >
                Start Your Journey
              </button>
            </div>
          </div>

          {/* Who It's For Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <EmotionalSupportCard 
              title="For those feeling socially overwhelmed" 
              description="Mind Polish provides a quiet, judgment-free space where you can process your thoughts without the pressure of social expectations."
              color="sage"
            />
            <EmotionalSupportCard 
              title="For scattered or racing thoughts" 
              description="Our tools help you gently organize your thoughts, one small piece at a time, without demanding perfect focus or linear thinking."
              color="mint"
            />
            <EmotionalSupportCard 
              title="For emotional burnout and shutdowns" 
              description="Mind Polish adapts to your energy levels, offering gentle support that doesn't demand more than you can give in the moment."
              color="cream"
            />
            <EmotionalSupportCard 
              title="For moments of loneliness" 
              description="Our compassionate tools offer warmth and understanding when you need connection without overwhelming social interaction."
              color="peach"
            />
            <EmotionalSupportCard 
              title="For future worries and uncertainty" 
              description="Mind Polish helps you focus on the present moment, offering gentle reassurance and practical support for navigating uncertainty."
              color="sage"
            />
            <EmotionalSupportCard 
              title="For work or study stress" 
              description="Our tools provide a calm space to decompress and process your stress, helping you find small moments of peace amid the pressure."
              color="mint"
            />
          </div>
        </section>

        {/* Tools Section */}
        <section className="py-16 px-4 sm:px-8 bg-gradient-to-br from-mint-50/20 to-sage-25/20 dark:from-mint-900/20 dark:to-sage-800/20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-light text-mint-600 dark:text-mint-300 mb-8 text-center">
              Our Gentle Wellbeing Tools
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <ToolCard 
                title="Routine Builder"
                description="Create gentle daily habits that adapt to your energy levels, with no pressure or guilt when you need to take it slow."
                icon="🌱"
                color="mint"
              />
              <ToolCard 
                title="Breathing Space"
                description="A peaceful breathing exercise that helps you find calm when everything feels too much."
                icon="🌸"
                color="sage"
              />
              <ToolCard 
                title="Gratitude Journal"
                description="A quiet place to notice small moments of peace or comfort, with no pressure to feel positive."
                icon="🌾"
                color="cream"
              />
              <ToolCard 
                title="Mood Check-In"
                description="Gently track your emotional state without judgment, helping you notice patterns and needs."
                icon="🌙"
                color="peach"
              />
            </div>
          </div>
        </section>

        {/* Dopa Section */}
        <section className="py-16 px-4 sm:px-8 bg-gradient-to-br from-peach-50/20 to-cream-25/20 dark:from-peach-900/20 dark:to-sage-800/20">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-8 mb-12">
              <div className="md:w-1/2">
                <div className="mx-auto mb-6 md:mx-0">
                  <DopaLogo size="lg" withText={true} />
                </div>
                <h2 className="text-2xl sm:text-3xl font-light text-peach-600 dark:text-peach-300 mb-4">
                  Meet Dopa, One of Our Supportive Tools
                </h2>
                <p className="text-peach-500 dark:text-peach-400 font-light text-base leading-relaxed mb-6">
                  Dopa is our AI voice companion that listens without judgment, adapts to your needs, and offers gentle support as part of our comprehensive mental wellness toolkit.
                </p>
                <button 
                  onClick={navigateToHome}
                  className="px-6 py-3 bg-peach-100/60 hover:bg-peach-200/50 
                             dark:bg-peach-900/30 dark:hover:bg-peach-800/40
                             text-peach-600 dark:text-peach-300 rounded-2xl text-base font-light 
                             border border-peach-200/40 dark:border-peach-700/30 
                             hover:border-peach-300/50 dark:hover:border-peach-600/40
                             transition-colors duration-300 focus:outline-none focus:ring-4 
                             focus:ring-peach-100/50 dark:focus:ring-peach-700/30"
                >
                  Enter App
                </button>
              </div>
              <div className="md:w-1/2 bg-white/60 dark:bg-sage-800/60 rounded-3xl border border-peach-100/30 dark:border-peach-800/30 p-6">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-peach-100/40 dark:bg-peach-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-peach-400 dark:text-peach-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-peach-600 dark:text-peach-300 font-light text-sm">
                        "I'm feeling overwhelmed today..."
                      </p>
                      <p className="text-peach-400 dark:text-peach-500 font-light text-xs mt-1">
                        You
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      <DopaLogo size="sm" />
                    </div>
                    <div>
                      <p className="text-peach-600 dark:text-peach-300 font-light text-sm">
                        "I hear that you're feeling overwhelmed. That's completely valid. Let's take a moment to breathe together. What's one small thing that feels most pressing right now?"
                      </p>
                      <p className="text-peach-400 dark:text-peach-500 font-light text-xs mt-1">
                        Dopa
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 px-4 sm:px-8 bg-gradient-to-br from-sage-50/20 to-mint-25/20 dark:from-sage-900/20 dark:to-mint-900/20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-light text-sage-600 dark:text-sage-300 mb-8 text-center">
              Voices from Our Community
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Testimonial 
                quote="Mind Polish has been a sanctuary for me. The tools adapt to my energy levels, and I love how everything feels designed with care."
                name="Alex"
                description="Living with social anxiety"
              />
              <Testimonial 
                quote="As someone with ADHD, I appreciate how Mind Polish doesn't demand perfect focus. The Routine Builder has been especially helpful for creating gentle structure."
                name="Jordan"
                description="ADHD experiencer"
              />
              <Testimonial 
                quote="During my burnout, I couldn't handle one more demanding thing. Mind Polish's low-energy modes and quiet spaces gave me room to breathe."
                name="Sam"
                description="Recovering from burnout"
              />
              <Testimonial 
                quote="The combination of different tools helps me manage my mental health in a way that feels supportive rather than overwhelming."
                name="Taylor"
                description="Living with depression"
              />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 sm:px-8 bg-gradient-to-br from-mint-50/20 to-sage-25/20 dark:from-mint-900/20 dark:to-sage-800/20">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-light text-sage-600 dark:text-sage-300 mb-6">
              Begin Your Wellbeing Journey Today
            </h2>
            <p className="text-lg font-light text-sage-500 dark:text-sage-400 mb-8 leading-relaxed">
              Join thousands who are finding comfort, clarity, and compassionate support through Mind Polish's gentle approach to mental wellbeing.
            </p>
            <button 
              onClick={navigateToHome}
              className="px-8 py-4 bg-sage-100/80 hover:bg-sage-200/60 
                         dark:bg-sage-700/40 dark:hover:bg-sage-600/50
                         text-sage-600 dark:text-sage-200 rounded-2xl text-lg font-light 
                         border border-sage-200/50 dark:border-sage-600/40 
                         hover:border-sage-300/60 dark:hover:border-sage-500/50
                         transition-colors duration-300 focus:outline-none focus:ring-4 
                         focus:ring-sage-100 dark:focus:ring-sage-600/30
                         shadow-sm hover:shadow-md"
            >
              Start Your Free Journey
            </button>
            <p className="text-sage-400 dark:text-sage-500 font-light text-sm mt-4">
              No account required. All features are free.
            </p>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-white/40 dark:bg-sage-800/40 backdrop-blur-sm border-t border-sage-100/30 dark:border-sage-700/30 p-6 mt-auto">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-8">
              <div>
                <h3 className="text-sage-600 dark:text-sage-300 font-light text-sm mb-3">About</h3>
                <ul className="space-y-2">
                  <li>
                    <button 
                      onClick={() => navigate('/about')}
                      className="text-sage-500 dark:text-sage-400 text-xs font-light hover:text-sage-600 dark:hover:text-sage-300 transition-colors"
                    >
                      Our Story
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => navigate('/about')}
                      className="text-sage-500 dark:text-sage-400 text-xs font-light hover:text-sage-600 dark:hover:text-sage-300 transition-colors"
                    >
                      Our Approach
                    </button>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-sage-600 dark:text-sage-300 font-light text-sm mb-3">Resources</h3>
                <ul className="space-y-2">
                  <li>
                    <button 
                      onClick={() => navigate('/support')}
                      className="text-sage-500 dark:text-sage-400 text-xs font-light hover:text-sage-600 dark:hover:text-sage-300 transition-colors"
                    >
                      Support
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => navigate('/disclaimer')}
                      className="text-sage-500 dark:text-sage-400 text-xs font-light hover:text-sage-600 dark:hover:text-sage-300 transition-colors"
                    >
                      Disclaimer
                    </button>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-sage-600 dark:text-sage-300 font-light text-sm mb-3">Legal</h3>
                <ul className="space-y-2">
                  <li>
                    <button 
                      onClick={() => navigate('/privacy')}
                      className="text-sage-500 dark:text-sage-400 text-xs font-light hover:text-sage-600 dark:hover:text-sage-300 transition-colors"
                    >
                      Privacy
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => navigate('/terms')}
                      className="text-sage-500 dark:text-sage-400 text-xs font-light hover:text-sage-600 dark:hover:text-sage-300 transition-colors"
                    >
                      Terms
                    </button>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-sage-600 dark:text-sage-300 font-light text-sm mb-3">Connect</h3>
                <ul className="space-y-2">
                  <li>
                    <button 
                      onClick={() => navigate('/contact')}
                      className="text-sage-500 dark:text-sage-400 text-xs font-light hover:text-sage-600 dark:hover:text-sage-300 transition-colors"
                    >
                      Contact Us
                    </button>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="text-center pt-4 border-t border-sage-100/30 dark:border-sage-700/30">
              <div className="mb-4">
                <MindPolishLogo size="sm" className="inline-flex justify-center" withText={false} />
              </div>
              <p className="text-sage-400 dark:text-sage-500 text-xs font-light">
                Mind Polish • Built with care for your wellbeing • {new Date().getFullYear()}
              </p>
            </div>
          </div>
        </footer>
      </div>
    </NaturalBackground>
  )
}

// Emotional Support Card Component
const EmotionalSupportCard = ({ title, description, color }: { title: string, description: string, color: 'sage' | 'mint' | 'cream' | 'peach' }) => {
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  
  const bgColor = {
    sage: isDark 
      ? 'bg-sage-800/60 hover:bg-sage-700/50 border-sage-700/40 hover:border-sage-600/50' 
      : 'bg-sage-50/60 hover:bg-sage-100/50 border-sage-100/40 hover:border-sage-200/50',
    mint: isDark 
      ? 'bg-mint-900/40 hover:bg-mint-800/50 border-mint-800/40 hover:border-mint-700/50' 
      : 'bg-mint-50/60 hover:bg-mint-100/50 border-mint-100/40 hover:border-mint-200/50',
    cream: isDark 
      ? 'bg-cream-900/40 hover:bg-cream-800/50 border-cream-800/40 hover:border-cream-700/50' 
      : 'bg-cream-50/60 hover:bg-cream-100/50 border-cream-100/40 hover:border-cream-200/50',
    peach: isDark 
      ? 'bg-peach-900/40 hover:bg-peach-800/50 border-peach-800/40 hover:border-peach-700/50' 
      : 'bg-peach-50/60 hover:bg-peach-100/50 border-peach-100/40 hover:border-peach-200/50'
  }
  
  const textColor = {
    sage: isDark ? 'text-sage-300' : 'text-sage-600',
    mint: isDark ? 'text-mint-300' : 'text-mint-600',
    cream: isDark ? 'text-cream-300' : 'text-cream-600',
    peach: isDark ? 'text-peach-300' : 'text-peach-600'
  }
  
  const descColor = {
    sage: isDark ? 'text-sage-400' : 'text-sage-500',
    mint: isDark ? 'text-mint-400' : 'text-mint-500',
    cream: isDark ? 'text-cream-400' : 'text-cream-500',
    peach: isDark ? 'text-peach-400' : 'text-peach-500'
  }
  
  return (
    <div className={`${bgColor[color]} rounded-2xl p-6 border transition-all duration-300 hover:shadow-md`}>
      <h3 className={`${textColor[color]} font-light text-base sm:text-lg mb-3`}>{title}</h3>
      <p className={`${descColor[color]} font-light text-sm leading-relaxed`}>{description}</p>
    </div>
  )
}

// Tool Card Component
const ToolCard = ({ title, description, icon, color }: { title: string, description: string, icon: string, color: 'sage' | 'mint' | 'cream' | 'peach' }) => {
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  
  const bgColor = {
    sage: isDark 
      ? 'bg-sage-800/60 border-sage-700/40 hover:bg-sage-700/50 hover:border-sage-600/50' 
      : 'bg-sage-50/60 border-sage-100/40 hover:bg-sage-100/50 hover:border-sage-200/50',
    mint: isDark 
      ? 'bg-mint-900/40 border-mint-800/40 hover:bg-mint-800/50 hover:border-mint-700/50' 
      : 'bg-mint-50/60 border-mint-100/40 hover:bg-mint-100/50 hover:border-mint-200/50',
    cream: isDark 
      ? 'bg-cream-900/40 border-cream-800/40 hover:bg-cream-800/50 hover:border-cream-700/50' 
      : 'bg-cream-50/60 border-cream-100/40 hover:bg-cream-100/50 hover:border-cream-200/50',
    peach: isDark 
      ? 'bg-peach-900/40 border-peach-800/40 hover:bg-peach-800/50 hover:border-peach-700/50' 
      : 'bg-peach-50/60 border-peach-100/40 hover:bg-peach-100/50 hover:border-peach-200/50'
  }
  
  const textColor = {
    sage: isDark ? 'text-sage-300' : 'text-sage-600',
    mint: isDark ? 'text-mint-300' : 'text-mint-600',
    cream: isDark ? 'text-cream-300' : 'text-cream-600',
    peach: isDark ? 'text-peach-300' : 'text-peach-600'
  }
  
  const descColor = {
    sage: isDark ? 'text-sage-400' : 'text-sage-500',
    mint: isDark ? 'text-mint-400' : 'text-mint-500',
    cream: isDark ? 'text-cream-400' : 'text-cream-500',
    peach: isDark ? 'text-peach-400' : 'text-peach-500'
  }
  
  return (
    <div className={`${bgColor[color]} rounded-2xl p-6 border transition-all duration-300 hover:shadow-md cursor-pointer`}>
      <div className="flex items-start space-x-4">
        <div className="text-2xl">{icon}</div>
        <div>
          <h3 className={`${textColor[color]} font-light text-lg mb-2`}>{title}</h3>
          <p className={`${descColor[color]} font-light text-sm leading-relaxed`}>{description}</p>
        </div>
      </div>
    </div>
  )
}

// Testimonial Component
const Testimonial = ({ quote, name, description }: { quote: string, name: string, description: string }) => {
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  
  return (
    <div className={`${isDark ? 'bg-sage-800/60 border-sage-700/40' : 'bg-white/60 border-sage-100/40'} rounded-2xl p-6 border`}>
      <p className={`${isDark ? 'text-sage-300' : 'text-sage-600'} font-light text-sm leading-relaxed mb-4 italic`}>"{quote}"</p>
      <div>
        <p className={`${isDark ? 'text-sage-300' : 'text-sage-600'} font-light text-sm`}>{name}</p>
        <p className={`${isDark ? 'text-sage-500' : 'text-sage-400'} font-light text-xs`}>{description}</p>
      </div>
    </div>
  )
}

export default Landing